import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { WizardState } from './types';

interface ReasonCodeStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
}

const OVERRIDE_TYPE_OPTIONS = [
  { label: 'Seasonal promotion', code: '01' },
  { label: 'Markdown of aged inventory', code: '10' },
  { label: 'Markdown of excess inventory', code: '11' },
  { label: 'Manual price for specialty and/or vintage items', code: '09' },
  { label: 'Other', code: '05' },
];

const ReasonCodeStep = ({ state, updateState }: ReasonCodeStepProps) => {
  const handleOverrideTypeChange = (type: string) => {
    const selectedOption = OVERRIDE_TYPE_OPTIONS.find(opt => opt.label === type);
    const prefix = selectedOption?.code || '';
    const reasonCode = prefix && state.overrideCodeSuffix ? `${prefix}-${state.overrideCodeSuffix}` : '';

    updateState({
      overrideType: type,
      reasonCode,
    });
  };

  const handleOverrideCodeChange = (suffix: string) => {
    const selectedOption = OVERRIDE_TYPE_OPTIONS.find(opt => opt.label === state.overrideType);
    const prefix = selectedOption?.code || '';
    const reasonCode = prefix && suffix ? `${prefix}-${suffix}` : '';

    updateState({
      overrideCodeSuffix: suffix,
      reasonCode,
    });
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        What type of override is this, and which override code should be applied to the template rows?
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            select
            label="Override type"
            InputLabelProps={{ shrink: true }}
            value={state.overrideType}
            onChange={e => handleOverrideTypeChange(e.target.value)}
            fullWidth
            size="small"
            required
          >
            {OVERRIDE_TYPE_OPTIONS.map(option => (
              <MenuItem key={option.code} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6}>
          <TextField
            label="Override code"
            placeholder="Enter a unique code for this override/markdown"
            InputLabelProps={{ shrink: true }}
            value={state.overrideCodeSuffix}
            onChange={e => handleOverrideCodeChange(e.target.value)}
            fullWidth
            size="small"
            required
          />
        </Grid>
      </Grid>

      <Alert severity="warning" sx={{ mt: 2 }}>
        Rows with a non-zero override price require an override type and override code before the file can be uploaded.
      </Alert>
    </Box>
  );
};

export default ReasonCodeStep;
